import { useState, useEffect } from 'react';
import { api } from '../../../services/api';
import { formatPrice } from '../../../utils/format';

interface Modele {
  id: number;
  nom: string;
  prix: number;
  image_url: string;
  categorie_nom?: string;
  description?: string;
}

interface Props {
  currentModeleId?: string | number;
  onSelect: (modele: Modele) => void;
  onClose: () => void;
}

export default function ModeleSelectModal({ currentModeleId, onSelect, onClose }: Props) {
  const [modeles, setModeles] = useState<Modele[]>([]);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const token = localStorage.getItem('atelier_bassoum_token');
    if (token) {
      api.getModeles(token)
        .then(data => setModeles(data))
        .finally(() => setLoading(false));
    }
  }, []);

  const filtered = modeles.filter(m =>
    !search || 
    m.nom.toLowerCase().includes(search.toLowerCase()) || 
    (m.categorie_nom && m.categorie_nom.toLowerCase().includes(search.toLowerCase()))
  );

  return (
    <div className="fixed inset-0 bg-black/60 z-[60] flex items-center justify-center p-4 backdrop-blur-sm" onClick={onClose}>
      <div className="bg-white rounded-3xl w-full max-w-2xl max-h-[85vh] flex flex-col shadow-2xl overflow-hidden animate-in fade-in zoom-in duration-200" onClick={e => e.stopPropagation()}>
        {/* Header */}
        <div className="px-8 py-6 border-b border-gray-100 flex items-center justify-between flex-shrink-0 bg-white">
          <div>
            <h3 className="font-playfair font-bold text-2xl text-gray-900">Catalogue des Modèles</h3>
            <p className="text-sm text-gray-400 mt-1">Choisissez un modèle pour cette commande</p>
          </div>
          <button onClick={onClose} title="Fermer" className="w-11 h-11 flex items-center justify-center rounded-2xl hover:bg-gray-100 cursor-pointer text-gray-400 transition-all hover:rotate-90">
            <i className="ri-close-line text-2xl" />
          </button>
        </div>

        {/* Search */}
        <div className="px-8 py-4 bg-gray-50/50 border-b border-gray-100 flex-shrink-0">
          <div className="relative">
            <div className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 flex items-center justify-center text-gray-400">
              <i className="ri-search-line text-lg" />
            </div>
            <input
              type="text"
              value={search}
              onChange={e => setSearch(e.target.value)}
              placeholder="Rechercher par nom ou catégorie…"
              className="w-full pl-12 pr-6 py-3.5 text-base border-2 border-gray-100 rounded-2xl focus:outline-none focus:border-foret-400 bg-white transition-all shadow-sm"
              autoFocus
            />
          </div>
        </div>

        {/* Categories / Grid */}
        <div className="flex-1 overflow-y-auto px-8 py-6 bg-gray-50/30">
          {loading ? (
            <div className="flex flex-col items-center justify-center py-32">
              <div className="w-12 h-12 border-4 border-foret-500 border-t-transparent rounded-full animate-spin mb-4" />
              <p className="text-gray-500 font-medium">Chargement du catalogue...</p>
            </div>
          ) : filtered.length === 0 ? (
            <div className="text-center py-32 text-gray-300">
               <i className="ri-search-eye-line text-6xl block mb-4 opacity-20" />
               <p className="text-lg font-medium">Aucun modèle ne correspond à votre recherche</p>
            </div>
          ) : (
            <div className="grid grid-cols-2 md:grid-cols-3 gap-5">
              {filtered.map(modele => {
                const isSelected = String(currentModeleId) === String(modele.id);
                return (
                  <button
                    key={modele.id}
                    onClick={() => onSelect(modele)}
                    className={`group relative flex flex-col rounded-3xl overflow-hidden border-2 transition-all text-left shadow-sm hover:shadow-xl ${
                      isSelected
                        ? 'border-foret-500 ring-4 ring-foret-50'
                        : 'border-white bg-white hover:border-foret-200'
                    }`}
                  >
                    <div className="aspect-[4/5] overflow-hidden bg-gray-100 relative">
                      <img 
                        src={api.getProxyUrl(modele.image_url)} 
                        alt={modele.nom} 
                        className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500" 
                      />
                      {modele.categorie_nom && (
                        <span className="absolute top-3 left-3 px-3 py-1 bg-white/90 backdrop-blur-sm text-[10px] font-black text-gray-600 rounded-full uppercase tracking-widest shadow-sm">
                          {modele.categorie_nom}
                        </span>
                      )}
                      {isSelected && (
                        <div className="absolute inset-0 bg-foret-600/20 flex items-center justify-center backdrop-blur-[2px]">
                           <div className="w-12 h-12 rounded-full bg-foret-500 text-white flex items-center justify-center shadow-2xl animate-in zoom-in duration-300">
                              <i className="ri-check-line text-2xl" />
                           </div>
                        </div>
                      )}
                    </div>
                    <div className="p-4 bg-white">
                      <h4 className="text-sm font-bold text-gray-900 group-hover:text-foret-700 transition-colors truncate">{modele.nom}</h4>
                      <p className="text-foret-600 font-extrabold text-sm mt-0.5">
                        {formatPrice(modele.prix)}
                      </p>
                    </div>
                  </button>
                );
              })}
            </div>
          )}
        </div>

        {/* Footer info */}
        <div className="px-8 py-5 border-t border-gray-100 bg-gray-50 flex items-center justify-between text-xs text-gray-400 font-medium tracking-tight">
           <p>{filtered.length} modèles disponibles</p>
           <button onClick={onClose} className="text-gray-500 hover:text-gray-700 font-bold uppercase tracking-wider cursor-pointer">Fermer</button>
        </div>
      </div>
    </div>
  );
}
